/**
 * @file useShipmentActions.ts
 * @description Действия над отгрузками заказа (phase19.12 шаги 5–6).
 *
 * Один обработчик на все кнопки: блокировка на время запроса, человеческий
 * тост и перечитывание списка после мутации (списки не правим руками).
 *
 * Ни одна ошибка отсюда наружу не уходит: любая — и от запроса, и от
 * последующего обновления списка — превращается в toast. Иначе отклонённый
 * промис долетал бы до общего обработчика и подменял экран заказа страницей
 * ошибки (например, на 422 «Отгрузка уже передана в СДЭК»).
 */

import { useCallback, useState } from "react";

import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  downloadShipmentPrintForm,
  sendShipmentToSdek,
  type ShipmentPrintForm,
} from "../../lib/api-carriers-sdek";
import { deleteShipment, shipmentAction, updateShipment } from "../../lib/api-orders";
import { saveBlob } from "../../lib/download";
import { syncShipmentAndReport } from "../../lib/shipment-sync-report";

/** Какая печатная форма сейчас готовится и у какой отгрузки. */
export interface PrintingState {
  shipmentId: string;
  form: ShipmentPrintForm;
}

export interface ShipmentActions {
  /** Идёт запрос — кнопки на карточках блокируются. */
  acting: boolean;
  printing: PrintingState | null;
  sendToSdek: (shipmentId: string) => void;
  syncSdek: (shipmentId: string) => void;
  ship: (shipmentId: string) => void;
  deliver: (shipmentId: string) => void;
  savePickupPoint: (shipmentId: string, code: string, address: string) => void;
  /** Трек-номер руками — для перевозчиков без интеграции (phase19.15). */
  saveTrackNumber: (shipmentId: string, trackNumber: string) => void;
  print: (shipmentId: string, form: ShipmentPrintForm) => void;
  /** Удалить черновик; `onDone` вызовется после запроса — закрыть диалог. */
  remove: (shipmentId: string, onDone?: () => void) => void;
}

/**
 * Итог действия: строка — обычный успех, объект — когда запрос прошёл, но
 * новость плохая (заявку отклонил перевозчик) или нейтральная.
 */
type ActionOutcome =
  | string
  | { severity: "success" | "error" | "info"; message: string };

/** Человеческий текст ошибки: ApiError отдаёт message сервера. */
function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function useShipmentActions(refresh: () => Promise<void> | void): ShipmentActions {
  const notify = useNotify();
  const [acting, setActing] = useState(false);
  const [printing, setPrinting] = useState<PrintingState | null>(null);

  const run = useCallback(
    (fn: () => Promise<ActionOutcome>, after?: () => void): void => {
      setActing(true);
      void (async () => {
        try {
          const outcome = await fn();
          if (typeof outcome === "string") notify.success(outcome);
          else notify[outcome.severity](outcome.message);
        } catch (err) {
          notify.error(errorText(err, "Не удалось выполнить действие"));
        }
        try {
          await refresh();
        } catch {
          // Список перечитаем при следующем действии — падать из-за этого нельзя.
        }
        setActing(false);
        // Закрываем диалог подтверждения и на успехе, и на ошибке: текст
        // ошибки уже показан тостом, держать модалку не за чем.
        after?.();
      })();
    },
    [notify, refresh],
  );

  const print = useCallback(
    (shipmentId: string, form: ShipmentPrintForm): void => {
      setPrinting({ shipmentId, form });
      void (async () => {
        try {
          const file = await downloadShipmentPrintForm(shipmentId, form);
          saveBlob(file.blob, file.filename);
        } catch (err) {
          notify.error(errorText(err, "Не удалось получить PDF"));
        } finally {
          setPrinting(null);
        }
      })();
    },
    [notify],
  );

  return {
    acting,
    printing,
    print,
    sendToSdek: (id) =>
      run(async () => {
        const res = await sendShipmentToSdek(id);
        return res.mock
          ? `Mock-режим: track ${res.trackNumber}`
          : `Передано в СДЭК${res.trackNumber ? ` (трек ${res.trackNumber})` : ""}`;
      }),
    // Тост пересказывает итоги синка с сервера (`addedEvents`,
    // `currentStage`): менеджеру нужно «приехали новые события или нет», а не
    // «запрос прошёл».
    // Отказ по заявке приходит асинхронно — карточка перерисуется после
    // refresh внутри run, отгрузка снова станет черновиком.
    syncSdek: (id) => run(() => syncShipmentAndReport(id)),
    ship: (id) =>
      run(async () => {
        await shipmentAction.ship(id);
        return "Отгрузка отправлена";
      }),
    deliver: (id) =>
      run(async () => {
        await shipmentAction.deliver(id);
        return "Отгрузка доставлена";
      }),
    savePickupPoint: (id, code, address) =>
      run(async () => {
        await updateShipment(id, { pickupPointCode: code });
        return `ПВЗ сохранён: ${address}`;
      }),
    saveTrackNumber: (id, trackNumber) =>
      run(async () => {
        await updateShipment(id, { trackNumber });
        return `Трек-номер сохранён: ${trackNumber}`;
      }),
    remove: (id, onDone) =>
      run(async () => {
        await deleteShipment(id);
        return "Отгрузка удалена";
      }, onDone),
  };
}
